import React, { useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { toParcels } from 'utils/newOrder';
import {
  Adressable,
  CreateNewOrder,
  OrderPackageSize,
  ServiceBrand,
} from 'types/orderTypes';
import EditableAddress from '../order/components/editOrderComponents/EditableAddress';
import { EditAddress } from '../order/components/editOrderComponents/transformToEditOrderRequestObject';
import dayjs from 'dayjs';
import { Account } from '../../types/userTypes';
import { DropdownComponent } from '../../ReusableComponents/Dropdown/DropdownComponent';
import { isSameDate, isTimeString, isToday } from '../../utils/date';

interface NewOrderFormProps {
  order: CreateNewOrder;
  handleOrderUpdate: (order: CreateNewOrder) => void;
  accounts: Account[];
}

function updateIfExisting(update: string, existingValue: string) {
  if (update === undefined || update === null) {
    return existingValue;
  }
  return update;
}

function updateDetails<T extends Adressable>(
  addressable: T,
  update: Partial<EditAddress>
): T {
  return {
    ...addressable,
    name: updateIfExisting(update.name, addressable.name),
    email: updateIfExisting(update.email, addressable.email),
    phoneNumber: updateIfExisting(update.phone, addressable.phoneNumber),
    phoneCountryCode: updateIfExisting(
      update.areaCode,
      addressable.phoneCountryCode
    ),
    address: {
      ...addressable.address,
      streetName: updateIfExisting(
        update.streetName,
        addressable.address.streetName
      ),
      streetNumber: updateIfExisting(
        update.streetNumber,
        addressable.address.streetNumber
      ),
      postalCode: updateIfExisting(
        update.postalCode,
        addressable.address.postalCode
      ),
      city: updateIfExisting(update.city, addressable.address.city),
      country: updateIfExisting(update.country, addressable.address.country),
    },
  };
}

const windowTimeRanges = [
  '17:30-19:30',
  '19:30-21:30',
  '17:30-21:30',
  '19:00-22:00',
];

export const NewOrderForm = ({
  order,
  handleOrderUpdate,
  accounts,
}: NewOrderFormProps): JSX.Element => {
  const [minimumCelsius, setMinimumCelsius] = useState<number>(5);
  const [maximumCelsius, setMaximumCelsius] = useState<number>(20);

  const windowLength = dayjs(order.destination.deliveryWindow.end).diff(
    order.destination.deliveryWindow.start,
    'minute'
  );
  const temperatureControlled = order.parcels.some(
    parcel => parcel.constraints?.temperature
  );

  const handleTimeUpdate = (key: string, value: Date) => {
    switch (key) {
      case 'from':
        const newUntilDate = new Date(value);
        newUntilDate.setHours(order.destination.deliveryWindow.end.getHours());
        newUntilDate.setMinutes(
          order.destination.deliveryWindow.end.getMinutes()
        );
        newUntilDate.setSeconds(
          order.destination.deliveryWindow.end.getSeconds()
        );
        handleOrderUpdate({
          ...order,
          origin: {
            ...order.origin,
            pickupWindows: [
              {
                start: value,
                end: order.origin.pickupWindows[0].end,
              },
            ],
          },
          destination: {
            ...order.destination,
            deliveryWindow: {
              start: value,
              end: newUntilDate,
            },
          },
        });
        break;
      case 'to':
        const newFromDate = new Date(value);
        newFromDate.setHours(order.destination.deliveryWindow.start.getHours());
        newFromDate.setMinutes(
          order.destination.deliveryWindow.start.getMinutes()
        );
        newFromDate.setSeconds(
          order.destination.deliveryWindow.start.getSeconds()
        );
        handleOrderUpdate({
          ...order,
          origin: {
            ...order.origin,
            pickupWindows: [
              {
                start: order.origin.pickupWindows[0].start,
                end: value,
              },
            ],
          },
          destination: {
            ...order.destination,
            deliveryWindow: {
              start: order.destination.deliveryWindow.start,
              end: value,
            },
          },
        });
        break;
      default:
        break;
    }
  };

  const changeToToday = () => {
    const oldFromDate = dayjs(order.destination.deliveryWindow.start);
    const oldUntilDate = dayjs(order.destination.deliveryWindow.end);

    const newFromDate = new Date();
    newFromDate.setHours(oldFromDate.get('hour'));
    newFromDate.setMinutes(oldFromDate.get('minute'));
    newFromDate.setSeconds(oldFromDate.get('second'));

    const newUntilDate = new Date();
    newUntilDate.setHours(oldUntilDate.get('hour'));
    newUntilDate.setMinutes(oldUntilDate.get('minute'));
    newUntilDate.setSeconds(oldUntilDate.get('second'));

    handleOrderUpdate({
      ...order,
      destination: {
        ...order.destination,
        deliveryWindow: {
          start: newFromDate,
          end: newUntilDate,
        },
      },
    });
  };

  const changeToNextDay = () => {
    const newFromDate = dayjs(order.destination.deliveryWindow.start)
      .add(1, 'day')
      .toDate();
    const newUntilDate = dayjs(order.destination.deliveryWindow.end)
      .add(1, 'day')
      .toDate();
    handleOrderUpdate({
      ...order,
      destination: {
        ...order.destination,
        deliveryWindow: {
          start: newFromDate,
          end: newUntilDate,
        },
      },
    });
  };

  const isSelectedTimeRange = (rangeString: string) => {
    const [fromTime, untilTime] = rangeString.split(/-/, 2);

    return (
      isTimeString(order.destination.deliveryWindow.start, fromTime) &&
      isTimeString(order.destination.deliveryWindow.end, untilTime) &&
      isSameDate(
        order.destination.deliveryWindow.start,
        order.destination.deliveryWindow.end
      )
    );
  };

  const setSelectedTimeRange = (rangeString: string) => {
    const [from, until] = rangeString.split(/-/, 2);
    const baseDate = order.destination.deliveryWindow.start;

    const [fromH, fromM] = from.split(/:/, 2);
    const fromDate = dayjs(baseDate)
      .hour(parseInt(fromH, 10))
      .minute(parseInt(fromM, 10))
      .toDate();

    const [untilH, untilM] = until.split(/:/, 2);
    const untilDate = dayjs(baseDate)
      .hour(parseInt(untilH, 10))
      .minute(parseInt(untilM, 10))
      .toDate();

    handleOrderUpdate({
      ...order,
      destination: {
        ...order.destination,
        deliveryWindow: {
          start: fromDate,
          end: untilDate,
        },
      },
    });
  };

  const handlePackageSizeUpdate = (size: OrderPackageSize) => {
    let weight = 0;
    switch (size) {
      case OrderPackageSize.Small:
        weight = 3000;
        break;
      case OrderPackageSize.Medium:
        weight = 6000;
        break;
      case OrderPackageSize.Large:
        weight = 21000;
        break;
      case OrderPackageSize.Xxl:
        weight = 100000;
        break;
      default:
        // default to LARGE
        weight = 21000;
        break;
    }

    const parcels = toParcels(1, weight, size);
    handleOrderUpdate({ ...order, parcels });
  };

  const handleParcelCount = (count: number) => {
    if (count > 0) {
      const parcel = order.parcels[0];
      const parcels = new Array(count).fill(parcel);

      handleOrderUpdate({ ...order, parcels });
    }
  };

  const handleTemperatureControlledClick = (temperatureControlled: boolean) => {
    const { parcels } = order;

    if (parcels.length > 0) {
      const parcel = order.parcels[0];
      const parcelsWithTemperatureControl = new Array(parcels.length).fill({
        ...parcel,
        constraints: temperatureControlled
          ? {
              temperature: {
                minimumCelsius,
                maximumCelsius,
              },
            }
          : undefined,
      });

      handleOrderUpdate({ ...order, parcels: parcelsWithTemperatureControl });
    }
  };

  const handleTemperatureControlChange = (min: number, max: number) => {
    const { parcels } = order;

    if (parcels.length > 0) {
      const parcel = order.parcels[0];
      const parcelsWithTemperatureControl = new Array(parcels.length).fill({
        ...parcel,
        constraints: temperatureControlled
          ? {
              temperature: {
                minimumCelsius: min,
                maximumCelsius: max,
              },
            }
          : undefined,
      });

      handleOrderUpdate({ ...order, parcels: parcelsWithTemperatureControl });
    }

    setMinimumCelsius(min);
    setMaximumCelsius(max);
  };

  return (
    <div>
      <div className="information__box">
        <span className="box__field">
          <h5>Delivery Brand:</h5>
          <div className="selector" style={{ fontWeight: 400 }}>
            <div
              className={
                order.brand === ServiceBrand.PORTERBUDDY
                  ? 'selected-porterbuddy'
                  : ''
              }
              onClick={() => {
                handleOrderUpdate({
                  ...order,
                  brand: ServiceBrand.PORTERBUDDY,
                });
              }}
            >
              Porterbuddy
            </div>
            <div
              className={
                order.brand === ServiceBrand.INSTABOX_HD
                  ? 'selected-instabox'
                  : ''
              }
              onClick={() => {
                handleOrderUpdate({
                  ...order,
                  brand: ServiceBrand.INSTABOX_HD,
                });
              }}
            >
              Instabox
            </div>
          </div>
        </span>
      </div>
      <div className="information__box">
        <span className="box__field">
          <h5>Sender Account:</h5>
          <div>
            <DropdownComponent
              className="accountid-selector"
              id="accountIdSelect"
              labelId={null}
              onChange={event => {
                handleOrderUpdate({
                  ...order,
                  accountId: Number(event.target.value),
                });
              }}
              value={order.accountId}
              itemsToSelect={accounts.map(account => {
                return {
                  name: `${account.name} - ${account.email}`,
                  value: account.id,
                };
              })}
            />
          </div>
        </span>
      </div>
      <div style={{ height: 10 }} />
      <div className="new-order__form">
        <div>
          <EditableAddress
            header="Sender"
            name={order.origin.name}
            email={order.origin.email}
            streetName={order.origin.address.streetName}
            streetNumber={order.origin.address.streetNumber}
            postalCode={order.origin.address.postalCode}
            city={order.origin.address.city}
            country={order.origin.address.country}
            areaCode={order.origin.phoneCountryCode}
            phone={order.origin.phoneNumber}
            updateAddress={updatedInfo => {
              handleOrderUpdate({
                ...order,
                origin: updateDetails(order.origin, updatedInfo),
              });
            }}
          />
        </div>
        <div>
          <EditableAddress
            header="Recipient"
            name={order.destination.name}
            email={order.destination.email}
            streetName={order.destination.address.streetName}
            streetNumber={order.destination.address.streetNumber}
            postalCode={order.destination.address.postalCode}
            city={order.destination.address.city}
            country={order.destination.address.country}
            areaCode={order.destination.phoneCountryCode}
            phone={order.destination.phoneNumber}
            updateAddress={updatedInfo => {
              handleOrderUpdate({
                ...order,
                destination: updateDetails(order.destination, updatedInfo),
              });
            }}
          />
        </div>
      </div>
      <div style={{ height: 30 }} />
      <div style={{ height: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        Deliver from:
        <div style={{ width: 10 }} />
        <Flatpickr
          onChange={selectedDates => {
            handleTimeUpdate('from', selectedDates[0]);
          }}
          value={order.destination.deliveryWindow.start}
          options={{
            dateFormat: 'd.m.y H:i',
            shorthandCurrentMonth: true,
            locale: { firstDayOfWeek: 1 },
            time_24hr: true,
            enableTime: true,
          }}
          render={(props, ref) => (
            <input className={'form-control'} type="text" ref={ref} />
          )}
        />
        <div style={{ width: 10 }} />
        Deliver until:
        <div style={{ width: 10 }} />
        <Flatpickr
          onChange={selectedDates => {
            handleTimeUpdate('to', selectedDates[0]);
          }}
          value={order.destination.deliveryWindow.end}
          options={{
            dateFormat: 'd.m.y H:i',
            shorthandCurrentMonth: true,
            locale: { firstDayOfWeek: 1 },
            time_24hr: true,
            enableTime: true,
          }}
          render={(props, ref) => (
            <input className={'form-control'} type="text" ref={ref} />
          )}
        />
      </div>
      <div className="information__box">
        <span className="box__field">
          <h5>Quick select: </h5>
          <div className="selector-wrapper">
            <div className="selector inline">
              <div
                className={
                  isToday(order.destination.deliveryWindow.start)
                    ? 'selected'
                    : ''
                }
                onClick={changeToToday}
              >
                Today
              </div>
              <div onClick={changeToNextDay}>Next day</div>
            </div>
            <div className="selector">
              {windowTimeRanges.map(timeRange => (
                <div
                  key={timeRange}
                  className={isSelectedTimeRange(timeRange) ? 'selected' : ''}
                  onClick={() => setSelectedTimeRange(timeRange)}
                >
                  {timeRange}
                </div>
              ))}
            </div>
          </div>
        </span>
      </div>
      <div style={{ height: 10 }} />
      <div
        style={
          windowLength !== 0 && windowLength < 60
            ? { backgroundColor: 'red' }
            : {}
        }
      >
        {` Window length: ${windowLength} min `}
        {windowLength < 60
          ? 'Delivery window must be greater than 60 minutes'
          : ''}
      </div>
      <div style={{ height: 30 }} />
      <input
        type="text"
        placeholder="Note"
        name="courierInstructions"
        onChange={event =>
          handleOrderUpdate({
            ...order,
            courierInstructions: event.target.value,
          })
        }
      />
      <div className="information__box">
        <span className="box__field">
          <h5>Delivery type: </h5>
          <div className="selector">
            <div
              className={order.product === 'delivery' ? 'selected' : ''}
              onClick={() => {
                handleOrderUpdate({ ...order, product: 'delivery' });
              }}
            >
              Scheduled
            </div>
            <div
              className={order.product === 'large' ? 'selected' : ''}
              onClick={() => {
                handleOrderUpdate({ ...order, product: 'large' });
              }}
            >
              Large (QBB)
            </div>
          </div>
        </span>
        <span className="box__field">
          <h5>Package size:</h5>
          <div className="selector">
            <div
              className={
                order.parcels[0].weightGrams === 3000 ? 'selected' : ''
              }
              onClick={() => {
                handlePackageSizeUpdate(OrderPackageSize.Small);
              }}
            >
              Small
            </div>
            <div
              className={
                order.parcels[0].weightGrams === 6000 ? 'selected' : ''
              }
              onClick={() => {
                handlePackageSizeUpdate(OrderPackageSize.Medium);
              }}
            >
              Medium
            </div>
            <div
              className={
                order.parcels[0].weightGrams === 21000 ? 'selected' : ''
              }
              onClick={() => {
                handlePackageSizeUpdate(OrderPackageSize.Large);
              }}
            >
              Large
            </div>
            <div
              className={
                order.parcels[0].weightGrams === 100000 ? 'selected' : ''
              }
              onClick={() => {
                handlePackageSizeUpdate(OrderPackageSize.Xxl);
              }}
            >
              Oversized
            </div>
          </div>
        </span>
        <span className="box__field">
          <h5>Package count:</h5>
          <div style={{ maxWidth: 50 }}>
            <input
              type="tel"
              placeholder={String(order.parcels.length)}
              name="parcelCount"
              onChange={event =>
                handleParcelCount(parseInt(event.currentTarget.value, 10))
              }
            />
          </div>
        </span>
        <span className="box__field">
          <h5>Temperature controlled: </h5>
          <div className="selector">
            <div
              className={temperatureControlled ? 'selected' : ''}
              onClick={() => {
                handleTemperatureControlledClick(true);
              }}
            >
              True
            </div>
            <div
              className={!temperatureControlled ? 'selected' : ''}
              onClick={() => {
                handleTemperatureControlledClick(false);
              }}
            >
              False
            </div>
          </div>
        </span>
        {temperatureControlled && (
          <span className="box__field">
            <h5>Temperature range: </h5>
            <div className="selector">
              <div style={{ maxWidth: 200, display: 'flex' }}>
                <span style={{ border: 'none', marginRight: 4 }}>Min: </span>
                <input
                  type="tel"
                  placeholder="From temp"
                  name="temp from"
                  value={minimumCelsius}
                  onChange={event =>
                    handleTemperatureControlChange(
                      parseInt(event.currentTarget.value, 10),
                      maximumCelsius
                    )
                  }
                />
                <span style={{ border: 'none', marginRight: 4, marginLeft: 4 }}>
                  Max:
                </span>
                <input
                  type="tel"
                  placeholder="To temp"
                  name="temp to"
                  value={maximumCelsius}
                  onChange={event =>
                    handleTemperatureControlChange(
                      minimumCelsius,
                      parseInt(event.currentTarget.value, 10)
                    )
                  }
                />
              </div>
            </div>
          </span>
        )}
        {temperatureControlled && order.parcels.length > 1 && (
          <div style={{ marginTop: 8 }}>
            All parcels will be temperature controlled
          </div>
        )}
        <div style={{ height: 32 }} />
      </div>
    </div>
  );
};
